// ==========================================================================
// usePrintDocumentV2.constants.js
// Shared constants for the V2 print pipeline ("decoupage").
//
// These are consumed by usePrintDocumentV2.js (planner/renderer),
// usePrintDocumentV2.utils.js (measurement, splitting, readiness), and
// the architecture doc
// (src/pages/report-workspace/V2/pagination/readme/print-v2-pagination-contract.md).
// ==========================================================================

// ─── Page Dimensions (CSS pixels, unzoomed) ──────────────────────────────────
// Re-exported from the pagination constants module so editor pagination and
// print planning share one source of truth.
export {
  PORTRAIT_PAGE_WIDTH,
  PORTRAIT_PAGE_HEIGHT,
  LANDSCAPE_PAGE_WIDTH,
  LANDSCAPE_PAGE_HEIGHT,
} from '../pages/report-workspace/V2/pagination';

// ─── Export Readiness ────────────────────────────────────────────────────────
// waitForExportReadiness() polls until fonts, images, and layout are stable
// before measurement begins. These tune its convergence behaviour.

/** Number of consecutive stable animation frames required before layout is considered settled. */
export const LAYOUT_STABLE_FRAMES = 6;

/** Maximum positional delta (px) between two layout signatures to consider them "matching". */
export const LAYOUT_DELTA_EPSILON = 0.25;

/** Total timeout budget (ms) shared across fonts, images, and layout stability checks (~⅓ each). */
export const EXPORT_READY_TIMEOUT_MS = 20000;

/** Minimum timeout budget (ms) for each readiness stage (fonts/images/layout). */
export const EXPORT_READY_MIN_STAGE_TIMEOUT_MS = 2000;

/** Minimum post-decoupage settle delay when final output contains real image sources. */
export const POST_DECOUPAGE_IMAGE_SETTLE_MS = 2000;

// ─── Editable Zone Print Auto-Fit ────────────────────────────────────────────
// Used by the print-iframe editable-zone revalidation pass to keep auto-resize
// behavior consistent between V1 and V2 pipelines.
export const PRINT_AUTOFIT_MIN_FONT_SIZE_PX = 8;
export const PRINT_AUTOFIT_MAX_ITERATIONS = 12;
export const PRINT_AUTOFIT_TOLERANCE_PX = 0.25;

// ─── Fragment Kind Sets ──────────────────────────────────────────────────────
// buildDecoupageContainers() classifies each planned fragment with a `kind`
// string. These sets group kinds by origin so the Phase 2 renderer can apply
// different clamping / suppression rules.
//
// CARRY kinds — fragments that were split/clipped on a previous page and
// carried forward. They are unconditionally clamped to writableTop.
//   splitCarryWhole          — carried content fits entirely on the new page
//   splitCarryTop            — re-split of carried content (top half placed here)
//   splitCarryNoResplit      — unsplittable carried content (clipped via overflow)
//   clipCarry                — clip-continuation rendered via translate offset
//   keepWithNextCarryWhole   — atomic keep-with-next block carried whole
//   imageCarryWhole          — atomic image block carried whole
//
// SOURCE kinds — fragments placed from source nodes assigned to the current
// page. They are only clamped if header intrusion exceeds the threshold.
//   whole                    — node fits entirely within the writable area
//   contractCurrentOnly      — editor decided node belongs only on current page
//   contractSplitTop         — editor-directed split (top half)
//   splitTop                 — inference-based split (top half)
//   unsplittableOnBoundary   — non-text node straddling boundary (clipped)
//   wholeBoundaryCurrentOnly — straddles boundary but all visible text is above cut

export const CARRY_FRAGMENT_KINDS = new Set([
  'splitCarryWhole',
  'splitCarryTop',
  'splitCarryNoResplit',
  'clipCarry',
  'keepWithNextCarryWhole',
  'imageCarryWhole',
]);

export const SOURCE_FRAGMENT_KINDS = new Set([
  'whole',
  'contractCurrentOnly',
  'contractSplitTop',
  'splitTop',
  'unsplittableOnBoundary',
  'wholeBoundaryCurrentOnly',
]);

/** Source fragments intruding into header space by more than this are clamped to writableTop. */
export const SOURCE_HEADER_CLAMP_THRESHOLD_PX = 12;

// ─── Editor Snapshot/Contract Toggles ────────────────────────────────────────
// These are intentionally hardcoded while print parity is tuned/debugged.
// Keeping them fixed makes output deterministic and simplifies isolation:
//   - disable SNAPSHOT to test raw iframe + decoupage only
//   - disable SNAPSHOT_GEOMETRY to test contract-only routing influence
//   - disable CONTRACT_DECISIONS to test inference-only planner behavior
// If we expose these as runtime config, do it as an explicit rollout.
export const APPLY_EDITOR_PAGINATION_SNAPSHOT = true;
export const APPLY_EDITOR_SNAPSHOT_GEOMETRY = true;
export const APPLY_EDITOR_CONTRACT_DECISIONS = true;

// Tolerate tiny sub-pixel overflow noise near page boundaries.
export const PAGE_BOUNDARY_EPSILON_PX = 1;
